import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ActionItem } from '../../components/panel-header/panel-header.component';
import { CreatePanelView, DetailPanelView } from '../../enums/list-panel/panel-view-mode.enum';

type ConfirmationPopupData = {
    title: string;
    message: string;
    confirmButtonText: string;
    isPermanentAction: boolean;
    id: string;
    confirmAction: () => Promise<void>;
};

type SortState = {
    sortMode: string;
    direction: 'asc' | 'desc';
};

/**
 * PanelMenuService provides centralized management of menu action items
 * for various panel headers throughout the application.
 */
@Injectable({
    providedIn: 'root'
})
export class PanelMenuService {
    detailsPanelViewStream = new BehaviorSubject<DetailPanelView>(DetailPanelView.Detail);
    createPanelViewStream = new BehaviorSubject<CreatePanelView>(CreatePanelView.Create);

    confirmationPopupDataStream = new BehaviorSubject<ConfirmationPopupData | null>(null);

    currentSortState: SortState = null;

    /**
     * Get action items for the survey job panel
     * @param showSearch Current search visibility state
     * @param showFilterPanel Current filter panel visibility state
     * @param sortApplied Whether sorting is currently applied
     * @param visibilityOn Whether visibility is currently on (true = visibility_on, false = visibility_off)
     * @returns Array of ActionItem objects
     */
    getSurveyJobPanelActions(
        showSearch: boolean,
        showFilterPanel: boolean,
        sortApplied: boolean,
        visibilityOn: boolean = true
    ): ActionItem[] {
        return [
            {
                id: 'search',
                iconClass: 'modus-icons',
                title: 'TC.Common.Search',
                custom: {
                    modusIcon: 'search',
                    active: showSearch
                }
            },
            {
                id: 'filter',
                iconClass: 'modus-icons',
                title: 'TC.Common.Filter',
                custom: {
                    modusIcon: 'filter',
                    active: showFilterPanel
                }
            },
            {
                id: 'sort',
                iconClass: 'modus-icons',
                title: 'sort',
                custom: {
                    modusIcon: 'sort',
                    active: sortApplied
                }
            },
            {
                id: 'add',
                iconClass: 'modus-icons i24',
                title: 'Survey.SurveyJobPanel.CreateAJob',
                custom: { modusIcon: 'add' }
            },
            {
                id: 'visibility',
                iconClass: visibilityOn ? 'i24 icon_line_visibility_show ' : 'i24 icon_line_visibility_hide',
                title: ''
            }
        ];
    }

    /**
     * Get action items for the survey job detail panel
     * @returns Array of ActionItem objects
     */
    getSurveyJobDetailActions(): ActionItem[] {
        return [
            {
                id: 'edit',
                iconClass: 'icon_line_task_edit_32 i24',
                title: 'TC.Common.Edit'
            }
            // TODO: Re-enable delete action when functionality is implemented
            // {
            //     id: 'delete',
            //     iconClass: 'icon_line_trash_red_32 i24',
            //     title: 'TC.Common.Delete'
            // }
        ];
    }

    /**
     * Update the active state of an action item
     * @param actions The array of action items
     * @param actionId The ID of the action to update
     * @param isActive The new active state
     */
    updateActionState(actions: ActionItem[], actionId: string, isActive: boolean): void {
        const action = actions.find(item => item.id === actionId);
        if (action && action.custom) {
            action.custom.active = isActive;
        }
    }

    /**
     * Update the current sort state
     * @param sortMode The sort mode
     * @param direction The sort direction
     */
    updateSortState(sortMode: string, direction: 'asc' | 'desc'): void {
        this.currentSortState = {
            sortMode,
            direction
        };
    }

    clearSortState(): void {
        this.currentSortState = null;
    }

    /**
     * Toggle the visibility icon between visibility_on and visibility_off
     * @param actions The array of action items
     * @param isVisible The new visibility state (true = visibility_on, false = visibility_off)
     */
    updateVisibilityIcon(actions: ActionItem[], isVisible: boolean): void {
        const visibilityAction = actions.find(item => item.id === 'visibility');
        visibilityAction.iconClass = isVisible ? 'i24 icon_line_visibility_show ' : 'i24 icon_line_visibility_hide';
    }
}
